<template>
    <main class="flex justify-center bg-gray-50">
      <HeaderComponent />
      <section class="w-full pb-[100px] lg:pl-[50px] lg:pt-[95px] pt-[95px] max-h-screen overflow-y-auto relative flex justify-start flex-col lg:p-[25px] p-[20px] h-auto min-h-screen">
        <!-- Notification messages - position ajustée pour le header de 280px -->
        <div class="fixed top-[80px] lg:left-[calc(50%+140px)] left-[50%] z-[100] translate-x-[-50%] w-[90%] max-w-[400px] flex flex-col">
            <div v-if="errorMessage" class="w-full rounded-lg text-white bg-red-500 my-2 font-medium text-center p-4 shadow-lg transition-all duration-300">
                {{ errorMessage }}
            </div>
            <div v-if="successMessage" class="w-full rounded-lg text-white bg-emerald-500 my-2 font-medium text-center p-4 shadow-lg transition-all duration-300">
                {{ successMessage }}
            </div>
        </div>

        <!-- Page header -->
        <div class="w-full mb-6 flex justify-between items-center">
          <h1 class="text-2xl font-bold text-gray-800">Gestion des Employés</h1>
          <button @click="toggleAddEmployeeForm" class="px-4 py-2 rounded-lg text-sm font-semibold flex items-center text-white bg-orange-500 hover:bg-orange-600 transition-colors shadow-md">
            <i class="fa-solid fa-plus mr-2"></i>
            Ajouter un employé
          </button>
        </div>

        <!-- Employees grid -->
        <div class="w-full">
          <div v-if="!isLoading && employes.length === 0" class="text-center w-full h-[300px] flex flex-col justify-center items-center">
            <i class="fa-solid fa-user-group text-4xl text-gray-300 mb-4"></i>
            <h2 class="text-lg font-semibold text-gray-500">Aucun employé trouvé</h2>
            <p class="text-sm text-gray-400 mt-2">Commencez par ajouter un nouvel employé</p>
          </div>

          <div v-if="isLoading" class="text-center h-[300px] w-full flex flex-col justify-center items-center">
            <div class="loader mb-4"></div>
            <p class="text-gray-500 font-medium">Chargement des employés...</p>
          </div>

          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div v-for="(employe, index) in employes" :key="index" class="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 overflow-hidden">
              <!-- Employee card header -->
              <div class="p-4 border-b border-gray-100 flex items-center">
                <div class="w-12 h-12 rounded-full bg-orange-50 flex-shrink-0 overflow-hidden flex items-center justify-center">
                  <img src="../assets/images/icone.png" class="w-full h-full object-cover" alt="Profile">
                </div>
                <div class="ml-3">
                  <h3 class="font-semibold text-gray-800">{{ employe.firstname }} {{ employe.lastname }}</h3>
                  <p class="text-xs text-gray-500">{{ employe.poste }} • {{ employe.boutique }}</p>
                </div>
                <button @click="toggleBox(employe._id)" class="ml-auto w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                  <i class="fa-solid fa-pen text-xs text-gray-600"></i>
                </button>
              </div>

              <!-- Employee details -->
              <div class="p-4">
                <div class="flex items-center mb-3">
                  <div class="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500 mr-2">
                    <i class="fa-solid fa-envelope text-sm"></i>
                  </div>
                  <p class="text-sm text-gray-600 truncate">{{ employe.email }}</p>
                </div>
                <div class="flex items-center mb-3">
                  <div class="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500 mr-2">
                    <i class="fa-solid fa-phone text-sm"></i>
                  </div>
                  <p class="text-sm text-gray-600">+229 {{ employe.phone }}</p>
                </div>
                <div class="flex items-center">
                  <div class="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-500 mr-2">
                    <i class="fa-solid fa-user text-sm"></i>
                  </div>
                  <p class="text-sm text-gray-600">{{ employe.username }}</p>
                </div>

                <!-- Status button -->
                <button 
                  @click="toggleActive(employe._id)" 
                  :class="{
                    'bg-red-100 text-red-700 hover:bg-red-200': employe.isActive,
                    'bg-green-100 text-green-700 hover:bg-green-200': !employe.isActive
                  }" 
                  class="w-full mt-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {{ employe.isActive ? 'Désactiver' : 'Activer' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Edit Employee Modal - position ajustée pour le header de 280px -->
        <div v-if="showBoxId" class="fixed inset-0 bg-black bg-opacity-50 lg:left-[280px] left-0 flex items-center justify-center z-50 p-4">
          <div class="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div class="p-6">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-xl font-bold text-gray-800">Modifier le profil</h3>
                <button @click="toggleBox(null)" class="text-gray-400 hover:text-gray-600">
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
              
              <form @submit.prevent="modifieEmployee(showBoxId)" class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Poste <span class="text-red-500">*</span></label>
                  <select v-model="poste" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                    <option value="Vendeur(se) Téléphone">Vendeur(se) Téléphone</option>
                    <option value="Vendeur(se) Accessoires">Vendeur(se) Accessoires</option>
                    <option value="Manager">Manager</option>
                    <option value="Vendeur(se) Accessoires et Téléphone">Vendeur(se) Accessoires et Téléphone</option>
                  </select>
                </div>
                
                <button 
                  type="submit" 
                  class="w-full bg-orange-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-orange-600 transition-colors flex justify-center items-center"
                  :disabled="isSubmitting"
                >
                  <span v-if="isSubmitting" class="loader-small mr-2"></span>
                  Modifier
                </button>
              </form>
            </div>
          </div>
        </div>

        <!-- Add Employee Form - position ajustée pour le header de 280px -->
        <div v-if="showAddEmployeeForm" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style="left: 280px;">
          <div class="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div class="p-6">
              <div class="flex justify-between items-center mb-4">
                <h3 class="text-xl font-bold text-gray-800">Ajouter un employé</h3>
                <button @click="toggleAddEmployeeForm" class="text-gray-400 hover:text-gray-600">
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
              
              <form @submit.prevent="addEmployee" class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Prénom <span class="text-red-500">*</span></label>
                    <input v-model="newEmployee.firstname" type="text" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" required>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Nom <span class="text-red-500">*</span></label>
                    <input v-model="newEmployee.lastname" type="text" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" required>
                  </div>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Email <span class="text-red-500">*</span></label>
                  <input v-model="newEmployee.email" type="email" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" required>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Téléphone <span class="text-red-500">*</span></label>
                  <input v-model="newEmployee.phone" type="text" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" required>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Poste <span class="text-red-500">*</span></label>
                  <select v-model="newEmployee.poste" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" required>
                    <option value="Vendeur(se) Téléphone">Vendeur(se) Téléphone</option>
                    <option value="Vendeur(se) Accessoires">Vendeur(se) Accessoires</option>
                    <option value="Manager">Manager</option>
                    <option value="Vendeur(se) Accessoires et Téléphone">Vendeur(se) Accessoires et Téléphone</option>
                  </select>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Boutique <span class="text-red-500">*</span></label>
                  <select v-model="newEmployee.boutique" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" required>
                    <option value="Calavi">Calavi</option>
                    <option value="Parakou">Parakou</option>
                  </select>
                </div>
                
                <button 
                  type="submit" 
                  class="w-full bg-orange-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-orange-600 transition-colors flex justify-center items-center"
                  :disabled="isSubmitting"
                >
                  <span v-if="isSubmitting" class="loader-small mr-2"></span>
                  Ajouter l'employé
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import HeaderComponent from '../components/HeaderComponent.vue';
import axios from 'axios';
import { useAuthStore } from '@/store/auth';
import { useRouter } from 'vue-router';
const router = useRouter();

const showBoxId = ref(null);
function toggleBox(id) {
  showBoxId.value = showBoxId.value === id ? null : id;
  if (id) {
    const employee = employes.value.find(e => e._id === id);
    if (employee) poste.value = employee.poste;
  }
}

const employes = ref([]);
const isLoading = ref(true);
const errorMessage = ref('');
const successMessage = ref('')
const showAddEmployeeForm = ref(false);
const isSubmitting = ref(false);
const poste = ref('')
const authStore = useAuthStore();
const user = computed(() => authStore.user);

const newEmployee = ref({
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  poste: '',
  boutique: '',
});

const toggleAddEmployeeForm = () => {
  showAddEmployeeForm.value = !showAddEmployeeForm.value;
  errorMessage.value = '';
};

const fetchEmployees = async () => {
  try {
    const response = await axios.get('/api/users');
    employes.value = response.data;
    isLoading.value = false;
  } catch (error) {
    errorMessage.value = 'Erreur lors du chargement des employés.';
    hideErrorMessageAfterDelay()
    isLoading.value = false;
  }
};

const addEmployee = async () => {
  isSubmitting.value = true;
  try {
    const response = await axios.post('/api/users', newEmployee.value);
    employes.value.push(response.data);
    toggleAddEmployeeForm();
    newEmployee.value = {
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      poste: '',
      boutique: '',
    };
    successMessage.value = "Employé ajouté avec succès";
    hideSuccessMessageAfterDelay();
  } catch (error) {
    errorMessage.value = error.response?.data?.message || 'Erreur lors de l\'ajout de l\'employé.';
    hideErrorMessageAfterDelay()
  } finally {
    isSubmitting.value = false;
  }
};

const toggleActive = async (id) => {
  try {
    await axios.put(`/api/users/${id}/toggle-active/`);
    const index = employes.value.findIndex((e) => e._id === id);
    employes.value[index].isActive = !employes.value[index].isActive;
    successMessage.value = `Employé ${employes.value[index].isActive ? 'activé' : 'désactivé'} avec succès`;
    hideSuccessMessageAfterDelay();
  } catch (error) {
    errorMessage.value = 'Erreur lors du changement de statut.';
    hideErrorMessageAfterDelay()
  }
};

const modifieEmployee = async (id) => {
  isSubmitting.value = true;
  try {
    await axios.put(`/api/update-poste/${id}`, {poste: poste.value});
    showBoxId.value = null;
    successMessage.value = "Profil de l'utilisateur modifié avec succès";
    hideSuccessMessageAfterDelay();
    fetchEmployees();
  } catch (error) {
    errorMessage.value = 'Erreur lors de la modification.';
    hideErrorMessageAfterDelay()
  } finally {
    isSubmitting.value = false;
  }
};

const hideSuccessMessageAfterDelay = () => {
    setTimeout(() => {
      successMessage.value = ''; 
    }, 5000);
};
  
const hideErrorMessageAfterDelay = () => {
    setTimeout(() => {
      errorMessage.value = ''; 
    }, 10000);
};

onMounted(() => {
  fetchEmployees();
  if(!user.value.isSuper) {
    router.push("/dashboard")
  }
});
</script>

<style scoped>
.loader {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #f97316;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

.loader-small {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #ffffff;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Smooth transitions for modals */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-content-enter-active,
.modal-content-leave-active {
  transition: all 0.3s ease;
}

.modal-content-enter-from,
.modal-content-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}
</style>